import { useState } from 'react';
import { useApp } from '../context/AppContext';
import { buildLeaderboard, ratingLabel } from '../../shared/logic/scoring';
import PickleballIcon from '../components/PickleballIcon';
import type { GameRecord, Player } from '../../shared/types';

type Tab = 'session' | 'league';

function Leaderboard({ players, games }: { players: Player[]; games: GameRecord[] }) {
  const lb = buildLeaderboard(players, games);
  if (!lb.some(p => p.gp > 0)) {
    return (
      <div className="empty">
        <div className="empty-icon">🏆</div>
        No games yet.
      </div>
    );
  }
  return (
    <table className="lb-table">
      <thead>
        <tr>
          <th>#</th>
          <th>Player</th>
          <th className="stat">Rating</th>
          <th className="stat">W</th>
          <th className="stat">L</th>
          <th className="stat">Win%</th>
          <th className="stat">Pts</th>
        </tr>
      </thead>
      <tbody>
        {lb.filter(p => p.gp > 0).slice(0, 10).map((p, i) => {
          const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : null;
          const rankClass = i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : '';
          return (
            <tr key={p.id}>
              <td className={`rank ${rankClass}`}>{medal || i + 1}</td>
              <td><strong>{p.name}</strong></td>
              <td className="stat">{ratingLabel(p)}</td>
              <td className="stat" style={{ color: 'var(--green)' }}>{p.wins}</td>
              <td className="stat" style={{ color: 'var(--red)' }}>{p.losses}</td>
              <td className="stat">{p.gp > 0 ? `${(p.wins / p.gp * 100).toFixed(0)}%` : '—'}</td>
              <td className="stat">{p.pts}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function RecentGames({ games, players, limit }: { games: GameRecord[]; players: Player[]; limit?: number }) {
  const playerName = (id: string) => players.find(p => p.id === id)?.name ?? id;
  const recent = [...games].reverse().slice(0, limit ?? games.length);
  if (!recent.length) {
    return (
      <div className="empty">
        <div className="empty-icon"><PickleballIcon size={64} /></div>
        No games yet.
      </div>
    );
  }
  return (
    <>
      {recent.flatMap(g =>
        g.courts.map((c, i) => (
          <div key={`${g.id}-${i}`} className="history-item">
            <div className="history-header">
              <div>
                <strong>{c.teamA.map(playerName).join(' & ')}</strong>
                <span style={{ color: 'var(--gray-500)' }}> vs </span>
                <strong>{c.teamB.map(playerName).join(' & ')}</strong>
              </div>
              {c.scoreA != null
                ? <strong>{c.scoreA}–{c.scoreB}</strong>
                : <span className="badge badge-gray">No score</span>
              }
            </div>
            <div className="history-meta">
              {new Date(g.date).toLocaleString()} · Round {g.round}
            </div>
          </div>
        ))
      )}
    </>
  );
}

export default function Dashboard() {
  const { league, players, games, sessionId } = useApp();
  const [activeTab, setActiveTab] = useState<Tab>('session');

  if (!league) return null;

  const sessionGames = games.filter(g => g.session === sessionId);
  const spotsLeft    = league.maxPlayers - players.length;
  const totalCourts  = games.reduce((s, g) => s + g.courts.length, 0);
  const sessionCourts = sessionGames.reduce((s, g) => s + g.courts.length, 0);

  const tabStyle = (t: Tab): React.CSSProperties => ({
    padding: '8px 20px',
    fontWeight: 600,
    fontSize: '0.9rem',
    border: 'none',
    borderBottom: activeTab === t ? '3px solid var(--yellow)' : '3px solid transparent',
    background: 'none',
    cursor: 'pointer',
    color: activeTab === t ? 'var(--text)' : 'var(--gray-500)',
  });

  return (
    <div className="page active" style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>

      {/* Stats */}
      <div className="stat-grid">
        <div className="stat-box">
          <div className="num">{players.length}</div>
          <div className="lbl">Registered</div>
        </div>
        <div className={`stat-box ${spotsLeft <= 5 ? 'warn' : ''}`}>
          <div className="num">{spotsLeft}</div>
          <div className="lbl">Spots Left</div>
        </div>
        <div className="stat-box">
          <div className="num">{activeTab === 'session' ? sessionCourts : totalCourts}</div>
          <div className="lbl">Games Played</div>
        </div>
        <div className="stat-box yellow">
          <div className="num">{league.courts}</div>
          <div className="lbl">Courts</div>
        </div>
      </div>

      {spotsLeft === 0 && (
        <div className="alert alert-red">🔒 Registration is <strong>full</strong> ({league.maxPlayers}/{league.maxPlayers}).</div>
      )}
      {spotsLeft > 0 && spotsLeft <= 5 && (
        <div className="alert alert-warn">⚠ Only <strong>{spotsLeft}</strong> spot{spotsLeft === 1 ? '' : 's'} left!</div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--gray-200)', marginBottom: 16 }}>
        <button style={tabStyle('session')} onClick={() => setActiveTab('session')}>
          This Session
        </button>
        <button style={tabStyle('league')} onClick={() => setActiveTab('league')}>
          League All-Time
        </button>
      </div>

      {/* Leaderboard */}
      <div className="card">
        <div className="card-title">🏆 Leaderboard</div>
        <Leaderboard
          players={players}
          games={activeTab === 'session' ? sessionGames : games}
        />
      </div>

      {/* Games */}
      <div className="card">
        <div className="card-title">
          <PickleballIcon size={20} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          {activeTab === 'session' ? 'This Session' : 'Recent Games'}
        </div>
        <RecentGames
          players={players}
          games={activeTab === 'session' ? sessionGames : games}
          limit={activeTab === 'league' ? 5 : undefined}
        />
      </div>
    </div>
  );
}
